import { createFileRoute } from "@tanstack/react-router";
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientStudioDocMetadata } from "api-types/studioDocs/studioDocMetadataTypes";
import type { ClientStudioDocHydrated } from "api-types/studioDocs/studioDocResponses";
import type { Doc } from "building-types/doc";
import { WEB_API_BASE_PATH } from "common/constants";
import downloadFile from "common/utils/downloadFile";
import AdminPage from "lib/components/admin/builders/AdminPage";
import DeleteStudioDocModal from "lib/components/admin/builders/DeleteStudioDocModal";
import BuilderSelectionModal from "lib/components/admin/homes/BuilderSelectionModal";
import BuilderHeader from "lib/components/builders/BuilderHeader";
import { getStudioDocsMetaDataForAdminURL } from "lib/endpointHelpers";
import { fetcher, useFetch, usePaginatedFetch } from "lib/fetcher";
import { studioPath, studioShowroomPath } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { useEffect, useMemo, useState } from "react";
import Button from "ui/buttons/Button";
import type { DropdownMenuItem } from "ui/components/DropdownMenu";
import { DropdownMenuAndButton } from "ui/components/DropdownMenu";
import Loading from "ui/components/Loading";
import Input from "ui/forms/Input";
import useAppContext from "ui/hooks/useAppContext";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import withAuthentication from "../../components/withAuthentication";
import styles from "./Homes.module.scss";

interface StudioDocDropdownProps {
  studioDoc: ClientStudioDocMetadata;
  setSelectedStudioDoc: (studioDoc: ClientStudioDocMetadata) => void;
  userIsStudioDocOwner: boolean;
}

function StudioDocDropdown({ studioDoc, setSelectedStudioDoc, userIsStudioDocOwner }: StudioDocDropdownProps) {
  const { setUserNotification } = useAppContext();

  const saveHomeFile = async (): Promise<void> => {
    try {
      const { FILE_EXT, docSerialize } = await import("doc/docMarshal");
      const data = await fetcher<ClientStudioDocHydrated>(`${WEB_API_BASE_PATH}/studio-docs/${studioDoc.id}`);
      const filename = studioDoc.name + FILE_EXT;
      const json = docSerialize(data.doc as Doc);

      downloadFile(filename, json, "application/json");
    } catch (e) {
      console.error(e);
      setUserNotification({ message: e?.message ?? "Failed to download .home file.", messageType: "error" });
    }
  };

  const menuItems: DropdownMenuItem[] = [
    {
      children: userIsStudioDocOwner ? "Configure in Studio" : "View in Studio",
      href: studioPath(studioDoc.id),
    },
    {
      children: "View in Showroom",
      href: studioShowroomPath(studioDoc.id),
    },
    {
      children: "Download .home file",
      onClick: saveHomeFile,
    },
  ];

  if (userIsStudioDocOwner) {
    menuItems.push({
      children: "Delete Home",
      onClick: () => setSelectedStudioDoc(studioDoc),
      disabled: !!(studioDoc.masterPlanCount && studioDoc.masterPlanCount > 0),
      title: "Studio Documents with dependent Master Plans cannot be deleted.",
    });
  }

  return <DropdownMenuAndButton menuItems={menuItems} />;
}

function AdminHomesPage() {
  const [hideUntitled, setHideUntitled] = useState(true);
  const [showMyPlansOnly, setShowMyPlansOnly] = useState(false);
  const { user } = useUserContext();
  const [isBuilderSelectionModalOpen, setIsBuilderSelectionModalOpen] = useState(false);
  const [selectedBuilder, setSelectedBuilder] = useState<ClientBuilder | null>(null);

  const apiUrl = useMemo(() => {
    return getStudioDocsMetaDataForAdminURL({
      hideUntitled,
      userId: showMyPlansOnly && user?.id ? user.id : undefined,
      builderId: selectedBuilder?.id,
    });
  }, [hideUntitled, showMyPlansOnly, user?.id, selectedBuilder?.id]);

  const {
    data: studioDocs,
    mutate,
    pagination,
    setPagination,
    loading,
    search,
    setSearch,
    orderBy,
    setOrderBy,
  } = usePaginatedFetch<ClientStudioDocMetadata>(apiUrl, [apiUrl]);

  //Reset pagination when filters change
  useEffect(() => {
    if (pagination) {
      setPagination({ ...pagination, pageIndex: 0 });
    }
  }, [setPagination, hideUntitled, showMyPlansOnly, selectedBuilder, search]);

  const { data: userOwnedStudioDocIds } = useFetch<string[]>(
    `${WEB_API_BASE_PATH}/users/me/studio-doc-ids?userRole=owner`,
  );
  const [selectedStudioDoc, setSelectedStudioDoc] = useState<ClientStudioDocMetadata | null>(null);

  const handleSelectBuilder = (builder: ClientBuilder | null) => {
    setSelectedBuilder(builder);
  };

  const homes = studioDocs?.data ?? [];

  const columns: Column<ClientStudioDocMetadata>[] = [
    { header: "Plan Name", field: "name", bold: true },
    { header: "Author", accessor: (p) => `${p.firstName} ${p.lastName} (${p.email})` },
    {
      header: "Published",
      field: "publishedAt",
      type: "DATE",
      fallback: true,
    },
    {
      header: "Updated",
      field: "updatedAt",
      type: "DATE",
      fallback: true,
    },
  ];

  return (
    <AdminPage currentPage="homes" pageTitle="Admin - Homes">
      <BuilderHeader
        header={selectedBuilder ? `Homes - ${selectedBuilder.companyName}` : "Homes"}
        headerActions={
          <>
            <div className={styles.searchContainer}>
              <Input
                className={styles.searchInput}
                inputSize="medium"
                placeholder="Search Homes"
                onChange={(e) => setSearch(e.currentTarget.value)}
                value={search}
              />
            </div>
            <div className={styles.filtersContainer}>
              <Button btnSize="medium" onClick={() => setIsBuilderSelectionModalOpen(true)}>
                {selectedBuilder ? `Builder: ${selectedBuilder.companyName}` : "Filter by Builder"}
              </Button>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  className={styles.checkboxInput}
                  checked={hideUntitled}
                  onChange={(e) => setHideUntitled(e.currentTarget.checked)}
                />
                Hide Untitled
              </label>
              <label className={styles.checkboxLabelMyHomes}>
                <input
                  type="checkbox"
                  className={styles.checkboxInput}
                  checked={showMyPlansOnly}
                  onChange={(e) => setShowMyPlansOnly(e.currentTarget.checked)}
                />
                My Homes
              </label>
            </div>
          </>
        }
      />
      <div className={styles.content}>
        {loading && !studioDocs?.data ? <Loading /> : null}

        {studioDocs?.data && (
          <div className={styles.wrapper}>
            {homes.length > 0 ? (
              <ConfigTable
                columns={columns}
                data={homes}
                pagination={pagination}
                setPagination={setPagination}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                totalCount={studioDocs.totalCount}
                getRowHref={(r) => studioPath(r.original.id)}
                overflowMenu={(row) => (
                  <StudioDocDropdown
                    studioDoc={row.original}
                    setSelectedStudioDoc={setSelectedStudioDoc}
                    userIsStudioDocOwner={
                      !!(userOwnedStudioDocIds && userOwnedStudioDocIds.indexOf(row.original.id) !== -1)
                    }
                  />
                )}
              />
            ) : (
              <div>{selectedBuilder ? `No homes found for ${selectedBuilder.companyName}` : "No homes found."}</div>
            )}
          </div>
        )}
      </div>
      {selectedStudioDoc !== null && (
        <DeleteStudioDocModal
          onClose={() => setSelectedStudioDoc(null)}
          studioDocName={selectedStudioDoc?.name}
          studioDocId={selectedStudioDoc?.id}
          onSuccess={async (studioDocId: string) => {
            if (studioDocs && studioDocs.data) {
              await mutate({
                ...studioDocs,
                data: studioDocs?.data?.filter((p) => p.id !== studioDocId),
              });
            }
          }}
        />
      )}

      <BuilderSelectionModal
        open={isBuilderSelectionModalOpen}
        onClose={() => setIsBuilderSelectionModalOpen(false)}
        onSelect={handleSelectBuilder}
        initialSelectedBuilderId={selectedBuilder?.id}
      />
    </AdminPage>
  );
}

export const Route = createFileRoute("/admin/homes")({
  component: withAuthentication(AdminHomesPage, { superAdmin: true }),
});
